import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePrivate from '@afosto/auth-react/usePrivate';
import useQueryParameters from '@afosto/utils-react/useQueryParameters';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PricingRuleForm from '../../components/PricingRuleForm';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import formatAdjustments from '../../utils/formatAdjustments';
import { pricing as pricingApi } from '@afosto/order/api/Pricing';
import translations from './translations';

const CreatePricingRule = () => {
  usePrivate();

  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useQueryParameters();
  const [isFetchingPricingRule, setIsFetchingPricingRule] = useState(true);
  const [pricingRule, setPricingRule] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchPricingRule = async pricingRuleId => {
      try {
        const response = await pricingApi.getPricingRule(pricingRuleId);
        const { data = {} } = response;

        if (isMounted) {
          setPricingRule({
            ...data,
            adjustments: formatAdjustments(data?.adjustments),
          });
        }
      } catch (error) {
        if (isMounted) {
          setPricingRule(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingPricingRule(false);
        }
      }
    };

    if (id) {
      fetchPricingRule(id);
    } else {
      setIsFetchingPricingRule(false);
    }

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleCreatePricingRule = async ({
    name,
    constraints,
    filters,
    adjustments,
    applyTo,
    grantLimit,
    priority,
    activeAt,
    expiresAt,
    isLastRule,
  }) => {
    try {
      await pricingApi.createPricingRule({
        name,
        constraints,
        filters,
        adjustments,
        apply_to: applyTo,
        grant_limit: grantLimit,
        priority,
        active_at: activeAt,
        expires_at: expiresAt,
        is_last_rule: isLastRule,
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.pricingRuleCreated), {
        variant: 'success',
      });

      history.push('/pricing-rules');
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.pricingRuleNotCreated), {
        variant: 'error',
      });
    }
  };

  if (isFetchingPricingRule) {
    return <SkeletonPage fullWidth />;
  }

  if (id && !pricingRule) {
    return <h1>Pricing rule not found!</h1>;
  }

  return (
    <Page title={intl.formatMessage(translations.title)}>
      <PricingRuleForm
        onSubmit={handleCreatePricingRule}
        onCancel={() => history.push('/pricing-rules')}
        pricingRule={pricingRule}
      />
    </Page>
  );
};

export default CreatePricingRule;
